import { useGetIntegrations } from "../../lib/hooks"
import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { client, validateClientProperty } from "@/lib/ts-rest"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { Calendar } from "../ui/calendar"
import { DateTime } from "luxon"
import {
  ArrowLeft,
  ChevronLeftIcon,
  ChevronRightIcon,
  Volume2Icon,
} from "lucide-react"
import type { PrayerTimeType } from "@speaker/database"
import { Switch } from "@/components/ui/switch"

export function Timetable({ integrationId }: { integrationId: number }) {
  const { data } = useGetIntegrations()
  const integration = data?.body.integrations.find(
    (item) => item.integration.id === integrationId
  )

  const [start, setStart] = useState<DateTime>()
  const [end, setEnd] = useState<DateTime>()
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const startIso = start?.toISO()
  const endIso = end?.toISO()
  const today = DateTime.now().setZone(integration?.timezone)

  useEffect(() => {
    if (integration?.timezone) {
      setStart(DateTime.now().setZone(integration.timezone).startOf("day"))
      setEnd(DateTime.now().setZone(integration.timezone).endOf("day"))
    }
  }, [integration])

  const { data: timesData } = client.getTimesForIntegrationByDay.useQuery({
    queryKey: [
      validateClientProperty("getTimesForIntegrationByDay"),
      integrationId,
      startIso,
      endIso,
    ],
    queryData: {
      params: {
        integrationId: String(integrationId),
      },
      query: {
        start: startIso!,
        end: endIso!,
      },
    },
    enabled:
      integration?.timezone !== undefined &&
      start !== undefined &&
      end !== undefined,
  })

  const times = timesData?.body

  function addOrdinal(number: number | undefined) {
    if (!number) return
    const suffixes = ["th", "st", "nd", "rd"]
    const v = number % 100
    const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]
    return `${number}${suffix}`
  }

  const timeTypes: PrayerTimeType[] = [
    "Fajr",
    "Sunrise",
    "Dhuhr",
    "Asr",
    "Maghrib",
    "Isha",
  ]
  const timeTypeToTime = new Map<
    PrayerTimeType,
    | NonNullable<NonNullable<typeof times>["timesByDate"][0]["times"]>[0]
    | undefined
  >()
  for (const timeType of timeTypes) {
    const time = times?.timesByDate
      .find((item) => item.date === start?.toISODate())
      ?.times?.find((item) => item.timeType === timeType)
    timeTypeToTime.set(timeType, time)
  }

  const dayBeforeStart = start?.minus({ days: 1 }).startOf("day")

  const [showHijrahSplitDate, setShowHijrahSplitDate] = useState(false)

  return (
    <div className="md:w-[300px]">
      <div className="flex flex-col">
        {/* Today */}
        <div className="mb-3 flex justify-center">
          <div
            onClick={() => {
              setStart(today.startOf("day"))
              setEnd(today.endOf("day"))
            }}
            className={cn(
              "flex gap-1 items-center rounded border-primary uppercase p-1 text-xs w-fit text-primary font-medium border-[1.5px] cursor-pointer",
              start?.toISODate() === today.toISODate()
                ? "opacity-100 pointer-events-none"
                : "opacity-50"
            )}
          >
            {start?.toISODate() !== today.toISODate() && (
              <ArrowLeft className="w-3 h-3" />
            )}
            <div>TODAY</div>
          </div>
        </div>
        {/* Current date and arrows */}
        <div className="mb-3 flex justify-between items-center gap-5">
          <div>
            <ChevronLeftIcon
              onClick={() => {
                if (!start || !end) return
                setStart(start.minus({ days: 1 }).startOf("day"))
                setEnd(end.minus({ days: 1 }).endOf("day"))
              }}
              className="w-7 h-7 text-primary cursor-pointer"
            />
          </div>
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <div className="font-medium text-center cursor-pointer">
                <div className="text-primary -mb-1">
                  {start?.weekdayLong} {addOrdinal(start?.day)}{" "}
                  {start?.monthLong}
                </div>
                <div className="text-pillarscream">
                  {start
                    ?.reconfigure({ outputCalendar: "islamic-umalqura" })
                    .toFormat("LLLL d, y G")}
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent className="flex flex-col p-0" align="center">
              <Calendar
                mode="single"
                selected={start?.toJSDate()}
                onSelect={(date) => {
                  if (date && integration?.timezone) {
                    const selectedDate = DateTime.fromJSDate(date).setZone(
                      integration.timezone
                    )
                    setStart(selectedDate.startOf("day"))
                    setEnd(selectedDate.endOf("day"))
                    setIsCalendarOpen(false)
                  }
                }}
                initialFocus
              />
              <div className="p-3 border-t">
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="hijri-split" className="text-sm font-medium">
                    Show Hijri date after Maghrib
                  </label>
                  <Switch
                    id="hijri-split"
                    checked={showHijrahSplitDate}
                    onCheckedChange={async (checked) => {
                      setShowHijrahSplitDate(checked)
                      await new Promise((resolve) => setTimeout(resolve, 500))
                      setIsCalendarOpen(false)
                    }}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <div>
            <ChevronRightIcon
              onClick={() => {
                if (!start || !end) return
                setStart(start.plus({ days: 1 }).startOf("day"))
                setEnd(end.plus({ days: 1 }).endOf("day"))
              }}
              className="w-7 h-7 text-primary cursor-pointer"
            />
          </div>
        </div>
        {/* Times */}
        <div className="flex flex-col gap-3">
          {[...timeTypeToTime].map(([timeType, time]) => (
            <div key={timeType} className="contents">
              <div
                className={cn(
                  "px-4 py-2 border-[1.5px] rounded-xl text-primary flex justify-between gap-2 items-center h-10",
                  time?.dateTime &&
                    time.dateTime === times?.next?.dateTime &&
                    "border-2 border-primary font-semibold",
                  (times?.next?.dateTime &&
                    time?.dateTime &&
                    DateTime.fromISO(times?.next?.dateTime)
                      .diff(DateTime.fromISO(time.dateTime))
                      .as("minutes") <= 0) ||
                    (start &&
                      start.diff(today).as("minutes") < 0 &&
                      "[&>*]:opacity-40")
                )}
              >
                <div className="basis-1 grow">{timeType}</div>
                <div className="!opacity-100">
                  {time?.alexaStartedAudioAt !== undefined && (
                    <Volume2Icon className="h-4 w-auto text-green-600" />
                  )}
                </div>
                <div className="basis-1 grow flex justify-end">
                  {time?.dateTime &&
                    DateTime.fromISO(time.dateTime)
                      .setZone(integration?.timezone)
                      .toLocaleString(DateTime.TIME_24_SIMPLE)}
                </div>
              </div>
              {timeType === "Maghrib" && showHijrahSplitDate && (
                <div className="text-center font-medium text-pillarscream">
                  {start
                    ?.plus({ days: 1 })
                    .reconfigure({ outputCalendar: "islamic-umalqura" })
                    .toFormat("LLLL d, y G")}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  // return (
  //   <div>
  //     <div
  //       onClick={() => {
  //         if (!start || !end) return
  //         setStart(start.minus({ days: 1 }).startOf("day"))
  //         setEnd(end.minus({ days: 1 }).endOf("day"))
  //       }}
  //     >
  //       prev
  //     </div>
  //     <div
  //       onClick={() => {
  //         if (!start || !end) return
  //         setStart(start.plus({ days: 1 }).startOf("day"))
  //         setEnd(end.plus({ days: 1 }).endOf("day"))
  //       }}
  //     >
  //       next
  //     </div>
  //     {times.timesByDate.map((time) => (
  //       <div key={time.date}>
  //         {time.times?.length} times for {time.date}
  //       </div>
  //     ))}
  //   </div>
  // )
}
